import { ApiClient } from '~/utils/ApiClient';
import type { Envelope } from "~/types/Envelope";
import type { SignedInUser } from "~/types/SignedInUser";
import type { ReportSummaryItem } from "~/types/ReportSummaryItem";
import type { InventoryItem } from "~/types/InventoryItem";
import { useUserStore } from "~/stores/userStore";
import { navigateTo } from '#app';
import { ApiError } from "~/utils/ApiError";
import type { Report } from "~/types/Report";
import type { ResetPasswordCode } from "~/types/ResetPasswordCode";
import type { Client, ClientSummary } from "~/types/Client";
import type { User, UserSummary } from '~/types/User';
import type { ReportSummary } from '~/types/ReportSummary';
import type { ProductPricing } from '~/types/ProductPricing';
import type { InventoryImage } from "~/types/InventoryImage";

export class ApiService extends ApiClient {

    private static instance: ApiService | null = null;
    private userStore = useUserStore();


    constructor() {
        const runtimeConfig = useRuntimeConfig() as { public: { apiBaseUrl: string } };
        const url: string = runtimeConfig.public.apiBaseUrl;
        super(url);
    }

    static getInstance(): ApiService {
        if (!ApiService.instance) {
            ApiService.instance = new ApiService();
        }
        return ApiService.instance;
    }

    async signUp(name: String, email: String, whatsappNumber: String, password: String, acceptedTerms: Boolean): Promise<Envelope<SignedInUser | null>> {
        return await this.post('/signUp', { name, email, whatsappNumber, chosenPassword: password, acceptedTerms });
    }

    async signIn(email: String, password: String): Promise<Envelope<SignedInUser | null>> {
        return this.post('/signIn', { email, password });
    }

    async resetPasswordCaptureWhatsapp(whatsappNumber: String): Promise<Envelope<any>> {
        return this.post("/resetPasswordCaptureWhatsapp", { whatsappNumber });
    }

    async resetPasswordCaptureCode(whatsappNumber: String, verificationCode: String): Promise<Envelope<ResetPasswordCode>> {
        return this.post("/resetPasswordCaptureCode", { whatsappNumber, verificationCode });
    }

    async resetPasswordNewPassword(whatsappNumber: string, resetPasswordCode: string, newPassword: String,): Promise<Envelope<any>> {
        return this.post("/resetPasswordNewPassword", { whatsappNumber, newPassword, resetPasswordCode });
    }

    async fetchReports(query: string = '', page: number = 1, pageSize: number = 10): Promise<Envelope<ReportSummary>> {
        const params: Record<string, string> = {
            page: page.toString(),
            pageSize: pageSize.toString(),
        };

        if (query.trim()) {
            params.q = encodeURIComponent(query).replace(/%20/g, '+');
        }

        const queryString = Object.entries(params)
            .map(([key, value]) => `${key}=${value}`)
            .join('&');

        const envelope = await this.get<Envelope<ReportSummary>>(`/reports?${queryString}`);

        if (!envelope.success) {
            if (envelope.statusCode == 401) {
                this.userStore.signOut();
                navigateTo({ path: "/sign-in", query: { "session": "expired" } });
            }
            throw new ApiError(envelope);
        } else {
            envelope.data?.reports?.map((item: ReportSummaryItem) => {
                item.dateCreated = new Date(item.dateCreated);
                item.lastUpdated = new Date(item.lastUpdated);
            });
        }
        return envelope;
    }

    async fetchReport(reportId: string): Promise<Envelope<Report>> {
        return await this.get<Envelope<Report>>(`/report/${reportId}`);
    }

    async updateReport(report: Report): Promise<Envelope<Report>> {
        return await this.put<Envelope<Report>>(`/report/${report.id}`, report);
    }

    /**
     * @TODO: Create a generic error handler
     */
    async fetchReportInventory(reportId: string): Promise<Envelope<InventoryItem[]>> {

        const envelope = await this.get<Envelope<InventoryItem[]>>(`/report/${reportId}/inventory`);

        if (!envelope.success) {
            if (envelope.statusCode == 401) {
                this.userStore.signOut()
                navigateTo({ path: "/sign-in", query: { "session": "expired" } })
            }
        } else {
            envelope.data?.map((item: InventoryItem) => {
                item.dateCreated && (item.dateCreated = new Date(item.dateCreated))
                item.lastUpdated && (item.lastUpdated = new Date(item.lastUpdated))
            })
        }
        return envelope
    }

    async fetchProductPricing(query: string = '', limit: number = 10): Promise<Envelope<ProductPricing[]>> {
        const params: Record<string, string> = {};

        // Add search query only if it's provided
        if (query.trim()) {
            params.q = encodeURIComponent(query).replace(/%20/g, '+');
        }

        // Add limit parameter if provided
        if (limit) {
            params.limit = limit.toString();
        }

        const queryString = new URLSearchParams(params).toString();
        const envelope = await this.get<Envelope<ProductPricing[]>>(`/pricing${queryString ? '?' + queryString : ''}`);

        if (!envelope.success) {
            if (envelope.statusCode === 401) {
                this.userStore.signOut();
                navigateTo({ path: "/sign-in", query: { session: "expired" } });
            }
            throw new ApiError(envelope);
        } else {
            envelope.data?.map((item: ProductPricing) => {
                item.dateCreated && (item.dateCreated = new Date(item.dateCreated));
            });
        }

        return envelope;
    }

    async fetchProductPricingById(id: string): Promise<Envelope<ProductPricing>> {
        const envelope = await this.get<Envelope<ProductPricing>>(`/pricing/${id}`);

        if (!envelope.success) {
            if (envelope.statusCode === 401) {
                this.userStore.signOut();
                navigateTo({ path: "/sign-in", query: { session: "expired" } });
            }
            throw new ApiError(envelope);
        } else if (envelope.data?.dateCreated) {
            envelope.data.dateCreated = new Date(envelope.data.dateCreated);
        }

        return envelope;
    }

    async deleteInventoryItem(inventoryItemId: string): Promise<Envelope<String>> {

        const envelope = await this.delete<Envelope<String>>(`/report/inventory/${inventoryItemId}`);

        if (!envelope.success) {
            if (envelope.statusCode == 401) {
                this.userStore.signOut()
                navigateTo({ path: "/sign-in", query: { "session": "expired" } })
            }
        }
        return envelope
    }

    async saveInventoryItem(inventoryItem: InventoryItem): Promise<Envelope<InventoryItem>> {

        try {

            const envelope = inventoryItem.id
                ? await this.put<Envelope<InventoryItem>>(`/report/inventory/${inventoryItem.id}`, inventoryItem)
                : await this.post<Envelope<InventoryItem>>(`/report/inventory`, inventoryItem);

            if (!envelope.success) {
                if (envelope.statusCode == 401) {
                    this.userStore.signOut()
                    navigateTo({ path: "/sign-in", query: { "session": "expired" } })
                }

                throw new ApiError({
                    errorCode: envelope.errorCode,
                    errorMessage: envelope.errorMessage,
                    validationMessages: envelope.validationMessages,
                    statusCode: envelope.statusCode
                });
            }
            return envelope
        }
        catch (error) {
            if (error instanceof ApiError) {
                throw error;
            }
            throw new ApiError({
                errorMessage: 'An error occurred while saving the item'
            });
        }
    }

    async fetchClients(page: number = 1, pageSize: number = 10, query: string = ''): Promise<Envelope<ClientSummary>> {
        let url = `/clients?page=${page}&pageSize=${pageSize}`;
        if (query.trim()) {
            url += `&q=${encodeURIComponent(query).replace(/%20/g, '+')}`;
        }

        const envelope = await this.get<Envelope<ClientSummary>>(url);

        if (!envelope.success) {
            if (envelope.statusCode == 401) {
                this.userStore.signOut()
                navigateTo({ path: "/sign-in", query: { "session": "expired" } })
            }
            throw new ApiError(envelope)
        } else {
            envelope.data?.clients.map((client: Client) => {
                client.created && (client.created = new Date(client.created))
                client.lastUpdated && (client.lastUpdated = new Date(client.lastUpdated))
            })
        }
        return envelope
    }

    async fetchClient(clientId: string): Promise<Envelope<Client>> {
        const envelope = await this.get<Envelope<Client>>(`/client/${clientId}`);

        if (!envelope.success) {
            if (envelope.statusCode == 401) {
                this.userStore.signOut()
                navigateTo({ path: "/sign-in", query: { "session": "expired" } })
            }
            throw new ApiError(envelope)
        } else if (envelope.data) {
            envelope.data.created && (envelope.data.created = new Date(envelope.data.created))
            envelope.data.lastUpdated && (envelope.data.lastUpdated = new Date(envelope.data.lastUpdated))
        }
        return envelope
    }

    async saveClient(client: Client): Promise<Envelope<Client>> {
        try {
            let envelope: Envelope<Client>;

            if (client.id) {
                // Update existing client
                envelope = await this.put<Envelope<Client>>(`/client/${client.id}`, client);
            } else {
                // Create new client
                envelope = await this.post<Envelope<Client>>('/client', client);
            }

            if (!envelope.success) {
                if (envelope.statusCode == 401) {
                    this.userStore.signOut();
                    navigateTo({ path: "/sign-in", query: { "session": "expired" } });
                }

                // If there are validation errors, throw an ApiError
                throw new ApiError({
                    errorCode: envelope.errorCode,
                    errorMessage: envelope.errorMessage,
                    validationMessages: envelope.validationMessages,
                    statusCode: envelope.statusCode
                });
            }

            return envelope;
        } catch (error) {
            if (error instanceof ApiError) {
                throw error;
            }
            throw new ApiError({
                errorMessage: 'An error occurred while saving the client'
            });
        }
    }

    async deleteClient(clientId: string): Promise<Envelope<String>> {
        const envelope = await this.delete<Envelope<String>>(`/client/${clientId}`);

        if (!envelope.success) {
            if (envelope.statusCode == 401) {
                this.userStore.signOut()
                navigateTo({ path: "/sign-in", query: { "session": "expired" } })
            }
            throw new ApiError(envelope)
        }
        return envelope
    }

    async initiateConversation(clientId: string): Promise<Envelope<string>> {
        try {
            const envelope = await this.post<Envelope<string>>(`/client/initiate/${clientId}`, {});

            if (!envelope.success) {
                if (envelope.statusCode == 401) {
                    this.userStore.signOut()
                    navigateTo({ path: "/sign-in", query: { "session": "expired" } })
                }
                throw new ApiError(envelope)
            }

            return envelope;
        } catch (error) {
            console.error('Failed to initiate conversation:', error);
            throw error;
        }
    }

    async fetchUsers(page: number = 1, pageSize: number = 10, query: string = ''): Promise<Envelope<UserSummary>> {
        let url = `/users?page=${page}&pageSize=${pageSize}`;
        if (query.trim()) {
            url += `&q=${encodeURIComponent(query).replace(/%20/g, '+')}`;
        }

        const envelope = await this.get<Envelope<UserSummary>>(url);

        if (!envelope.success) {
            if (envelope.statusCode == 401) {
                this.userStore.signOut()
                navigateTo({ path: "/sign-in", query: { "session": "expired" } })
            }
            throw new ApiError(envelope)
        } else {
            envelope.data?.users?.map((user: User) => {
                user.created && (user.created = new Date(user.created))
                user.lastUpdated && (user.lastUpdated = new Date(user.lastUpdated))
            })
        }
        return envelope
    }

    async fetchInventoryImages(inventoryItemId: string): Promise<Envelope<InventoryImage[]>> {
        let url = `/report/inventoryItem/${inventoryItemId}/images`;
        const envelope = await this.get<Envelope<InventoryImage[]>>(url);

        if (!envelope.success) {
            if (envelope.statusCode == 401) {
                this.userStore.signOut()
                navigateTo({ path: "/sign-in", query: { "session": "expired" } })
            }
            throw new ApiError(envelope)
        }
        return envelope
    }

    async fetchUser(userId: string): Promise<Envelope<User>> {
        const envelope = await this.get<Envelope<User>>(`/user/${userId}`);

        if (!envelope.success) {
            if (envelope.statusCode == 401) {
                this.userStore.signOut()
                navigateTo({ path: "/sign-in", query: { "session": "expired" } })
            }
            throw new ApiError(envelope)
        } else if (envelope.data) {
            envelope.data.created && (envelope.data.created = new Date(envelope.data.created))
            envelope.data.lastUpdated && (envelope.data.lastUpdated = new Date(envelope.data.lastUpdated))
        }
        return envelope
    }

    async saveUser(user: User): Promise<Envelope<any>> {
        try {
            let envelope: Envelope<User>;

            if (user.id) {
                // Update existing user
                envelope = await this.put<Envelope<User>>(`/user/${user.id}`, user);
            } else {
                // Create new user
                envelope = await this.post<Envelope<User>>('/user', user);
            }

            if (!envelope.success) {
                if (envelope.statusCode == 401) {
                    this.userStore.signOut();
                    navigateTo({ path: "/sign-in", query: { "session": "expired" } });
                }

                // If there are validation errors, throw an ApiError
                throw new ApiError({
                    errorCode: envelope.errorCode,
                    errorMessage: envelope.errorMessage,
                    validationMessages: envelope.validationMessages,
                    statusCode: envelope.statusCode
                });
            }

            return envelope;
        } catch (error) {
            if (error instanceof ApiError) {
                throw error;
            }
            throw new ApiError({
                errorMessage: 'An error occurred while saving the user'
            });
        }
    }

    async deleteUser(userId: string): Promise<Envelope<String>> {
        const envelope = await this.delete<Envelope<String>>(`/user/${userId}`);

        if (!envelope.success) {
            if (envelope.statusCode == 401) {
                this.userStore.signOut()
                navigateTo({ path: "/sign-in", query: { "session": "expired" } })
            }
            throw new ApiError(envelope)
        }
        return envelope
    }

    async resetUserPassword(userId: string): Promise<Envelope<any>> {
        try {
            const envelope = await this.post<Envelope<any>>(`/user/${userId}/reset-password`, {});

            if (!envelope.success) {
                if (envelope.statusCode == 401) {
                    this.userStore.signOut()
                    navigateTo({ path: "/sign-in", query: { "session": "expired" } })
                }
                throw new ApiError({
                    errorCode: envelope.errorCode,
                    errorMessage: envelope.errorMessage,
                    validationMessages: envelope.validationMessages,
                    statusCode: envelope.statusCode
                });
            }

            return envelope;
        } catch (error) {
            console.error('Failed to reset user password:', error);
            throw error;
        }
    }

}
